import { useEffect, useRef, useState } from 'react';

type PanelEligibility = { eligible: boolean; redirectUrl: string | null };

type SigninDoneMessage = {
  type: 'TAP_CONNECT_SIGNIN_DONE';
  status: 'SUCCESS' | 'ERROR';
  error?: string;
  panelEligibility?: PanelEligibility;
};

const isSigninDoneMessage = (data: unknown): data is SigninDoneMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === 'TAP_CONNECT_SIGNIN_DONE';

const FollowUpForm = ({
  signinId,
  onDone,
}: {
  signinId?: string;
  onDone?: (
    status: 'SUCCESS' | 'ERROR',
    error?: string,
    panelEligibility?: PanelEligibility
  ) => void;
}) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !signinId) return;

    const handleLoad = () => {
      setIsIframeLoaded(true);
    };

    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [signinId]);

  // The iframe (same-origin) posts this message when its own form finishes,
  // since its response never reaches this component directly.
  useEffect(() => {
    if (!onDone) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isSigninDoneMessage(event.data)) return;
      onDone(event.data.status, event.data.error, event.data.panelEligibility);
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onDone]);

  return (
    <div className="relative">
      {(!isIframeLoaded || !signinId) && (
        <div className="flex items-center justify-center h-[386px]">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
      )}
      {!!signinId && (
        <iframe
          ref={iframeRef}
          src={`/signin/${signinId}`}
          className={`w-full rounded-xl transition-opacity duration-300 ${
            isIframeLoaded ? 'h-[386px] opacity-100' : 'h-[0px] opacity-0'
          }`}
        />
      )}
    </div>
  );
};

export default FollowUpForm;
